import PdfPreview from '@components/Customization/PdfPreview/PdfPreview';
import { toPreviewAnswer } from '@components/Customization/PdfPreview/previewAnswer';
import PreviewSource from '@components/Customization/PdfPreview/PreviewSource';
import { usePreviewSource } from '@components/Customization/PdfPreview/usePreviewSource';
import { toolbarStyles } from '@components/Customization/PoiDesigner/designer.styles';
import BrandLogoField from '@components/Customization/ReportBranding/BrandLogoField';
import BrandTextField from '@components/Customization/ReportBranding/BrandTextField';
import LayoutChoice from '@components/Customization/ReportBranding/LayoutChoice';
import ScopePicker, { ORGANIZATION } from '@components/Customization/ScopePicker';
import { previewCellStyles } from '@components/Customization/ReportDesigner/designer.styles';
import Header from '@components/Shared/Layout/Header';
import {
  applyDraft, builtInBrandTexts, fieldsOf, isFieldSet, pendingOf, stageLogo, stageText, toBrandingBody,
  toBrandingPatch, withoutSent, type ReportBrandingDraft,
} from '@lib/customization/reportBranding';
import { landingOwner } from '@lib/customization/landingOwner';
import { cleanText } from '@lib/customization/reportDesign';
import { keptDraftsOf, useAskBeforeLeaving } from '@lib/customization/unsavedWork';
import { useCustomizationLocales } from '@lib/customization/useCustomizationLocales';
import { useWriteChain, useWritesLeftBehind } from '@lib/customization/useWriteChain';
import { useLocalization } from '@lib/localization';
import { Box, Button, LinearProgress, Paper, Stack, Typography } from '@mui/material';
import { heroIconStyles, readOnlyBannerStyles } from '@pages/AppSettings.styles';
import { buttonStyles } from '@pages/CustomPoiTypes.styles';
import { issueStyles, layoutStyles, pickerNoteStyles } from '@pages/ReportBranding.styles';
import { resolveLabel } from '@store/api/poiTypeEndpoints';
import {
  brandableLayouts, brandingLayerKey, isBrandableLayout, reportBrandingLimits, useGetReportBrandingsQuery,
  usePatchReportBrandingMutation, usePreviewReportBrandingMutation, useUploadReportBrandingLogoMutation,
  type BrandableLayout, type ReportBrandingScope, type ReportBrandingTextKey,
} from '@store/api/reportBrandingEndpoints';
import { type ReportImageRef, type ReportText } from '@store/api/reportDesignEndpoints';
import { useGetTeamsQuery } from '@store/api/teamEndpoints';
import { useAppSelector, usePermission } from '@store/hooks';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ExclamationTriangleIcon, LockIcon, StampIcon } from 'react-line-awesome';
import { useSearchParams } from 'react-router-dom';

/** The texts of a brand in the order they are shown, with the key of each one's label. */
const textFields: { key: ReportBrandingTextKey; label: string }[] = [
  { key: 'brandName', label: 'ReportDesign_Field_BrandName' },
  { key: 'headerLabel', label: 'ReportBranding_Field_HeaderLabel' },
  { key: 'footerText', label: 'ReportBranding_Field_FooterText' },
];

const teamIdOf = (scope: string): string | null => (scope === ORGANIZATION ? null : scope);

/** The page's address: the owner of the layer, and the layout the preview shows it on once one was chosen. */
const addressOf = (scope: string, layout: BrandableLayout | null): Record<string, string> => ({
  ...(scope === ORGANIZATION ? { scope: ORGANIZATION } : { team: scope }),
  ...(layout ? { layout } : {}),
});

/**
 * Unsaved changes by layer (`brandingLayerKey`), for as long as the tab lives. Opening another
 * team, or leaving the page, unmounts what held them, and the router cannot ask first; kept
 * here, they are there again on the way back. They are their author's alone:
 * signing out does not reload the tab, so whoever signs in next starts with none. A reload
 * or a close of the tab asks first while a kept layer still holds something to save.
 */
const keptDrafts = keptDraftsOf<ReportBrandingDraft>('report-branding');

/** The name the page's writes are queued under, for whoever opens the page while some are on their way. */
const BRANDING_WRITES = 'report-branding';

/** Requests on their way, counted by the layer they are for. */
interface Busy {
  saves: Record<string, number>;
  uploads: Record<string, number>;
}

/**
 * The brand reports are printed with, in two layers: the organization's
 * (`?scope=organization`), which every team's sits on, and one team's (`?team=`) on top of
 * it. A field a layer does not set shows what is printed in its place: the organization's
 * under a team, else what a layout prints by itself. With no owner in the address the page
 * opens where `landingOwner` says: the organization for whoever may change it, and for
 * everybody else their default team, the one whose brand their own reports carry.
 *
 * The brand is one for every layout that takes it. `?layout=` only says which of them the
 * preview shows it on, and changes neither the fields nor the draft.
 *
 * Nothing is written until Save. A field changes a draft, the preview beside it is a report
 * as the server draws it with that draft, and Save sends the difference as one merge
 * patch: a field that was changed, and `null` for one that was taken off. A logo is sent the
 * moment it is picked, before the draft that names it; uploads and saves run one after
 * another, so a save asked for while a file is on its way carries its id.
 */
const ReportBrandingEditor = () => {
  const { t, currentLanguage } = useLocalization();
  const { can, canInAnyTeam, userId } = usePermission();
  const session = useAppSelector((state) => state.auth.user);
  const locales = useCustomizationLocales();
  const [searchParams, setSearchParams] = useSearchParams();

  // Whoever may read a team's brand may read the organization's: it is what that team's sits
  // on. Changing it takes the permission for every team, which no team role gives.
  const canOpen = canInAnyTeam('report_branding:get');

  const { data: teams, isLoading: isTeamsLoading } = useGetTeamsQuery(undefined, { skip: !canOpen });
  const {
    data: layers, isLoading: isLayersLoading, isError: isLayersError,
  } = useGetReportBrandingsQuery(undefined, { skip: !canOpen });
  const [patchBranding] = usePatchReportBrandingMutation();
  const [uploadLogo] = useUploadReportBrandingLogoMutation();
  const [previewBranding] = usePreviewReportBrandingMutation();

  // ---- What is on screen ----

  const isOrganization = canOpen && searchParams.get('scope') === ORGANIZATION;
  const readableTeams = useMemo(() => (teams ?? []).filter((team) => can('report_branding:get', team._id)), [teams, can]);
  const team = isOrganization ? undefined : readableTeams.find((candidate) => candidate._id === searchParams.get('team'));
  const scope = isOrganization ? ORGANIZATION : team?._id;

  // The layout the preview is drawn in. One the address does not name, or names wrongly, is
  // the first; the address is left alone.
  const layoutParam = searchParams.get('layout');
  const namedLayout = isBrandableLayout(layoutParam) ? layoutParam : null;
  const layout = namedLayout ?? brandableLayouts[0];

  const open = (nextScope: string, nextLayout: BrandableLayout | null) => setSearchParams(addressOf(nextScope, nextLayout));

  // No owner in the address, or a team this reader cannot open (a stale link, a team since
  // deleted), lands where the page would open by itself.
  const defaultTeamId = session?.defaultTeam?.id;
  const defaultTeamName = session?.teams.find((candidate) => candidate.id === defaultTeamId)?.name;
  const landing = useMemo(() => landingOwner({
    canEditOrganization: can('report_branding:patch'),
    canReadOrganization: canOpen,
    defaultTeamId,
    teamIds: readableTeams.map((candidate) => candidate._id),
  }), [can, canOpen, defaultTeamId, readableTeams]);

  useEffect(() => {
    if (!canOpen || isTeamsLoading || scope || landing === undefined) return;

    setSearchParams(addressOf(landing ?? ORGANIZATION, namedLayout), { replace: true });
  }, [canOpen, isTeamsLoading, scope, landing, namedLayout, setSearchParams]);

  const canEditScope = (id: string) => (id === ORGANIZATION
    ? can('report_branding:patch')
    : readableTeams.some((candidate) => candidate._id === id) && can('report_branding:patch', id));
  const canEdit = Boolean(scope) && canEditScope(scope ?? '');

  const target = useMemo(
    (): ReportBrandingScope => ({ teamId: isOrganization ? null : team?._id ?? null }),
    [isOrganization, team],
  );
  const layerKey = brandingLayerKey(target);

  // ---- The draft ----

  // The drafts, what is saved and what is on its way are state for the screen and refs for
  // the chain below: a write that waited its turn reads them as they are when it runs.
  const [drafts, setDrafts] = useState(() => keptDrafts.readAll(userId));
  const draftsRef = useRef(drafts);
  const layersRef = useRef(layers);
  const [busy, setBusy] = useState<Busy>({ saves: {}, uploads: {} });
  const busyRef = useRef(busy);
  // The ids the server has answered for with "no such image".
  const [missing, setMissing] = useState<ReadonlySet<string>>(() => new Set());
  const missingRef = useRef(missing);
  // The files picked during this visit, as addresses a picture can show.
  const [localImages, setLocalImages] = useState<Record<string, string>>({});
  const madeUrls = useRef<string[]>([]);
  const alive = useRef(true);
  const userIdRef = useRef(userId);

  layersRef.current = layers;
  missingRef.current = missing;
  userIdRef.current = userId;

  useEffect(() => {
    const made = madeUrls.current;

    alive.current = true;

    return () => {
      alive.current = false;
      made.forEach((url) => URL.revokeObjectURL(url));
      made.length = 0;
    };
  }, []);

  const stage = useCallback((key: string, change: (current: ReportBrandingDraft) => ReportBrandingDraft) => {
    draftsRef.current = { ...draftsRef.current, [key]: change(draftsRef.current[key] ?? {}) };
    setDrafts(draftsRef.current);

    // A write that ends after the page was left finds no effect to keep its outcome: the logo
    // an upload stored, or the fields a save has made true. It goes into the kept copy itself.
    // Whatever such a write leaves in the draft is still to be saved: a logo that was just
    // stored, or what was typed while the save was on its way.
    if (!alive.current) {
      const left = change(keptDrafts.read(userIdRef.current, key) ?? {});

      keptDrafts.leave(userIdRef.current, key, left, Object.keys(left).length > 0);
    }
  }, []);

  const track = useCallback((kind: keyof Busy, key: string, by: 1 | -1) => {
    const counts = busyRef.current[kind];

    busyRef.current = { ...busyRef.current, [kind]: { ...counts, [key]: (counts[key] ?? 0) + by } };
    setBusy(busyRef.current);
  }, []);

  // A value that is the saved one again leaves the draft. Not while a save of the layer is on
  // its way: what is saved is about to change under that comparison, so the value is kept as
  // it was typed and `pendingOf` says afterwards whether it is still a change.
  const setText = (key: string, field: ReportBrandingTextKey, value: ReportText | null) => stage(key, (current) => (
    busyRef.current.saves[key] ? { ...current, [field]: value } : stageText(current, fieldsOf(layersRef.current?.[key]), field, value)
  ));

  const setLogo = useCallback((key: string, value: ReportImageRef | null) => stage(key, (current) => (
    busyRef.current.saves[key] ? { ...current, logo: value } : stageLogo(current, fieldsOf(layersRef.current?.[key]), value)
  )), [stage]);

  // What this layer sets, and what the one beneath does: under a team the organization's
  // stored layer, which is what the server draws beneath a team's too, never its draft.
  const own = useMemo(() => fieldsOf(layers?.[layerKey]), [layers, layerKey]);
  const organization = useMemo(
    () => (target.teamId === null ? {} : fieldsOf(layers?.[brandingLayerKey({ teamId: null })])),
    [layers, target.teamId],
  );

  // What is not saved yet for the layer on screen, and the layer as its fields show it. A
  // draft counts only where it can still be saved.
  const draft = canEdit ? drafts[layerKey] : undefined;
  const pending = useMemo(() => pendingOf(draft ?? {}, own), [draft, own]);
  const shown = useMemo(() => applyDraft(own, draft ?? {}), [own, draft]);
  const pendingCount = Object.keys(pending).length;

  const hasUnsaved = (id: string) => {
    const key = brandingLayerKey({ teamId: teamIdOf(id) });

    return canEditScope(id) && Object.keys(pendingOf(drafts[key] ?? {}, fieldsOf(layers?.[key]))).length > 0;
  };

  const saving = (busy.saves[layerKey] ?? 0) > 0;
  const uploading = (busy.uploads[layerKey] ?? 0) > 0;

  // The layers that hold something to save, by key and as one text, so that the effect below
  // runs when the answer changes and not with every render.
  const unsavedKeys = [ORGANIZATION, ...readableTeams.map((candidate) => candidate._id)].filter(hasUnsaved)
    .map((id) => brandingLayerKey({ teamId: teamIdOf(id) })).join();

  // Every draft is kept as it is, with whether it holds something to save. That is decided
  // here, against the stored layers and what the reader may change, which nothing can look
  // up once the page has been left. Until what a draft is judged against has been read the
  // page cannot tell either, and what was said of the draft before stands: a reader who is
  // back and leaves again before it arrives is still asked about what they left.
  //
  // The organization's draft is judged against the layers alone, and only a team's waits for
  // the teams as well. A visit in which the teams could not be read still changes and saves
  // the organization's brand, and a draft that changes while nothing is said of it keeps a
  // flag that was true of another state: asked about after its save, or not asked about
  // after the change that followed. An empty draft waits for nothing: it holds nothing to
  // save whatever was read, and it is what a save or a discard leaves behind.
  const layersRead = Boolean(layers);
  const teamsRead = Boolean(teams);

  useEffect(() => {
    const unsaved = unsavedKeys.split(',');
    const organizationKey = brandingLayerKey({ teamId: null });

    const holdsUnsaved = (key: string, kept: ReportBrandingDraft): boolean | undefined => {
      if (Object.keys(kept).length === 0) return false;

      const judged = layersRead && (key === organizationKey || teamsRead);

      return judged ? unsaved.includes(key) : undefined;
    };

    Object.entries(drafts).forEach(([key, kept]) => keptDrafts.keep(userId, key, kept, holdsUnsaved(key, kept)));
  }, [userId, drafts, unsavedKeys, layersRead, teamsRead]);

  // Reloading or closing the tab is the one way a draft is lost, so that asks first: here
  // for the page on screen, a logo on its way included. Once the page has been left the same
  // is asked for the drafts it kept and for the writes it queued.
  const unsettled = unsavedKeys !== '' || Object.values(busy.uploads).some((count) => count > 0);

  useAskBeforeLeaving(unsettled);

  // ---- Writes: one at a time ----

  const enqueue = useWriteChain(BRANDING_WRITES);

  const reportMissing = useCallback((imageId: string) => {
    setMissing((current) => (current.has(imageId) ? current : new Set([...current, imageId])));
  }, []);

  // For the layer that was on screen when the file was picked, wherever the reader is by the
  // time it is stored.
  const upload = useCallback(async (owner: ReportBrandingScope, file: File): Promise<boolean> => {
    const key = brandingLayerKey(owner);

    track('uploads', key, 1);

    try {
      return await enqueue(async () => {
        const result = await uploadLogo({ ...owner, file });

        // The refusal has been shown by the endpoint: the server's own sentence.
        if (result.error !== undefined || !result.data) return false;

        const { imageId } = result.data;

        if (alive.current) {
          const url = URL.createObjectURL(file);

          madeUrls.current.push(url);
          setLocalImages((current) => ({ ...current, [imageId]: url }));
        }

        // Within this turn of the chain, so that a save waiting behind it finds the logo in the draft.
        setLogo(key, { imageId });

        return true;
      });
    } finally {
      track('uploads', key, -1);
    }
  }, [enqueue, uploadLogo, setLogo, track]);

  const save = () => {
    const owner = target;
    const key = layerKey;

    track('saves', key, 1);

    void enqueue(async () => {
      const sent = pendingOf(draftsRef.current[key] ?? {}, fieldsOf(layersRef.current?.[key]));

      // Nothing to send after all (the only change was an upload that failed), or the logo
      // turned out to be gone while this waited.
      if (Object.keys(sent).length === 0 || (sent.logo && missingRef.current.has(sent.logo.imageId))) return;

      const result = await patchBranding({ ...owner, patch: toBrandingPatch(sent) });

      if (result.error !== undefined) return;

      // What was sent is saved; a field changed while it was on its way stays unsaved, unless
      // it was changed to what the answer says the layer now holds.
      const stored = result.data;

      stage(key, (current) => {
        const left = withoutSent(current, sent);

        return stored ? pendingOf(left, fieldsOf(stored)) : left;
      });
    }).finally(() => track('saves', key, -1));
  };

  // ---- Preview ----

  // What a request carries: the layer as its fields show it for whoever may save it, nothing
  // for a reader, who is shown the stored layer. The picture also depends on the layer beneath
  // and on the layout it is drawn in.
  const body = useMemo(() => toBrandingBody(shown), [shown]);
  const live = useMemo(
    () => JSON.stringify([layerKey, layout, canEdit, body, toBrandingBody(organization)]),
    [layerKey, layout, canEdit, body, organization],
  );
  const source = usePreviewSource(live);

  const requestPreview = (signal: AbortSignal) => {
    const running = previewBranding({
      ...target, layout, draft: canEdit ? body : undefined, source: source.source, locale: source.locale,
    });

    signal.addEventListener('abort', () => running.abort(), { once: true });

    return running.then(toPreviewAnswer);
  };

  // A layer that names a logo the server no longer has cannot be drawn, and cannot be saved
  // with that logo in the patch.
  const logoMissing = Boolean(shown.logo && missing.has(shown.logo.imageId));
  const canSave = canEdit && (pendingCount > 0 || uploading) && !saving
    && !(pending.logo && missing.has(pending.logo.imageId));

  const builtIn = builtInBrandTexts(t, currentLanguage);

  const status = () => {
    if (saving) return t('ReportDesign_Editor_Saving');
    if (uploading) return t('ReportDesign_Editor_Uploading');

    return pendingCount > 0 ? t('Customization_Designer_Unsaved') : t('Customization_Designer_Saved');
  };

  // Nothing open yet: on its way to a team or the organization, or a reader who may open neither.
  if (!scope) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
        <Header />
        {canOpen ? (
          <LinearProgress sx={{ width: '100%' }} />
        ) : (
          <Paper variant="outlined">
            <Typography color="text.secondary" sx={{ p: 3, textAlign: 'center' }}>{t('ReportBranding_NoTeams')}</Typography>
          </Paper>
        )}
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
      <Header />

      <Paper variant="outlined" sx={{ mb: 2, overflow: 'hidden' }}>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} sx={{ p: 2.5 }} alignItems={{ md: 'flex-start' }}>
          <Stack direction="row" spacing={2} sx={{ flexGrow: 1, minWidth: 0 }}>
            <Box sx={heroIconStyles}><StampIcon /></Box>
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="h5" sx={{ fontWeight: 600 }}>{t('ReportBranding_Title')}</Typography>
              <Typography color="text.secondary" sx={{ mt: 0.5, maxWidth: 620 }}>
                {t(isOrganization ? 'ReportBranding_Subtitle_Organization' : 'ReportBranding_Subtitle')}
              </Typography>
            </Box>
          </Stack>

          <Box sx={{ flexShrink: 0 }}>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5, fontWeight: 600 }}>
              {t(canEdit ? 'AppSettings_Editing' : 'AppSettings_Viewing')}
            </Typography>
            <ScopePicker
              value={scope}
              teams={readableTeams}
              defaultTeamId={defaultTeamId}
              hasUnsaved={hasUnsaved}
              onChange={(next) => open(next, namedLayout)}
            />
            {/* The mark in the picker, in words: a brand follows who downloads a report, not what is printed. */}
            <Typography variant="caption" sx={pickerNoteStyles}>
              {defaultTeamName ? t('ReportBranding_DefaultTeam_Help', [defaultTeamName]) : t('ReportBranding_NoDefaultTeam')}
            </Typography>
          </Box>
        </Stack>
      </Paper>

      {!canEdit && (
        <Stack direction="row" sx={{ ...readOnlyBannerStyles, mb: 2 }}>
          <LockIcon />
          <span>{t(isOrganization ? 'ReportBranding_ReadOnly_Organization' : 'ReportBranding_ReadOnly_Team')}</span>
        </Stack>
      )}

      {isLayersLoading && <LinearProgress sx={{ width: '100%', my: 2 }} />}

      {/* Without the layers every field would read as not set, over a brand that is. A re-read that fails leaves what was read. */}
      {!isLayersLoading && isLayersError && !layers && (
        <Paper variant="outlined">
          <Typography color="text.secondary" sx={{ p: 3, textAlign: 'center' }}>{t('ReportBranding_LoadFailed')}</Typography>
        </Paper>
      )}

      {layers && (
        // `clip`, not `hidden`: a hidden overflow is a scroll container, and the preview's
        // `position: sticky` would stick to this box instead of the page.
        <Paper variant="outlined" sx={{ overflow: 'clip' }}>
          {canEdit && (
            <Stack direction="row" sx={toolbarStyles}>
              <Typography variant="caption" color={pendingCount > 0 || uploading ? 'warning.main' : 'text.secondary'}>
                {status()}
              </Typography>
              <Box sx={{ flex: 1 }} />
              <Button
                size="small" color="inherit" sx={buttonStyles}
                disabled={pendingCount === 0 || saving || uploading}
                onClick={() => stage(layerKey, () => ({}))}
              >
                {t('Customization_Designer_Discard')}
              </Button>
              <Button size="small" variant="contained" disableElevation sx={buttonStyles} disabled={!canSave} onClick={save}>
                {t('Common_Save')}
              </Button>
            </Stack>
          )}

          {logoMissing && (
            <Stack direction="row" sx={issueStyles}>
              <ExclamationTriangleIcon />
              <span>{t('ReportDesign_Issue_Image')}</span>
            </Stack>
          )}

          <Box sx={layoutStyles}>
            <Stack spacing={2.5} sx={{ minWidth: 0 }}>
              {/* Where the brand is printed, since it is no longer chosen here by layout. */}
              <Typography variant="caption" color="text.secondary">{t('ReportBranding_Layout_Help')}</Typography>

              {/* Another layer's fields start afresh: an open list of translations is not carried over to them. */}
              <Stack spacing={2.5} key={layerKey}>
                {textFields.map(({ key, label }) => {
                  const inherited = cleanText(organization[key]);

                  return (
                    <BrandTextField
                      key={key}
                      label={t(label)}
                      value={shown[key]}
                      maxLength={reportBrandingLimits[key]}
                      setHere={isFieldSet(shown, key)}
                      pending={key in pending}
                      beneathSource={inherited ? 'organization' : 'builtIn'}
                      beneath={inherited
                        ? t('AppSettings_OrganizationValue', [resolveLabel(inherited, locales)])
                        : t('ReportBranding_BuiltInValue', [builtIn[key]])}
                      disabled={!canEdit}
                      onChange={(text) => setText(layerKey, key, text)}
                      onRevert={() => setText(layerKey, key, null)}
                    />
                  );
                })}

                <BrandLogoField
                  scope={target}
                  value={shown.logo}
                  organizationLogo={organization.logo}
                  local={localImages}
                  missing={missing}
                  pending={'logo' in pending}
                  // Not while a logo of this layer is on its way either. The picker that took
                  // the file is gone once another owner was opened meanwhile, and the one
                  // mounted on the way back would offer Remove, which the upload then undoes.
                  disabled={!canEdit || uploading}
                  onPick={(file) => upload(target, file)}
                  onRemove={() => setLogo(layerKey, null)}
                  reportMissing={reportMissing}
                />
              </Stack>
            </Stack>

            <Box sx={previewCellStyles}>
              <PdfPreview
                request={requestPreview}
                revision={source.revision}
                valid={!logoMissing}
                // With a real inspection chosen the picture stays until Refresh, also when
                // another owner or layout is opened: it is then another brand, or another layout's page.
                stale={source.pending}
                toolbar={(
                  <>
                    <LayoutChoice value={layout} onChange={(next) => open(scope, next)} />
                    <PreviewSource control={source} />
                  </>
                )}
              />
            </Box>
          </Box>
        </Paper>
      )}
    </Box>
  );
};

/**
 * The page, once nothing an earlier visit sent is still on its way. A logo's upload and a
 * save go on after the page is left and write their outcome into the kept drafts; someone
 * who is back before that would start from drafts without it, and overwrite it with theirs.
 */
const ReportBrandingPage = () => {
  const waiting = useWritesLeftBehind(BRANDING_WRITES);

  if (waiting) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
        <Header />
        <LinearProgress sx={{ width: '100%' }} />
      </Box>
    );
  }

  return <ReportBrandingEditor />;
};

export default ReportBrandingPage;
